// ──────────────────────────────────────────────
// LPの中身をどの口にどう頼むか。ここが正本。
//
// 【なぜ別ファイルにしたか】
// 顧客サイトに置くタグは、小さな先導役（boot.ts）と描画本体（embed.ts）に分かれている。
// 先導役は、本体を読み込みながら同時に中身を頼みに行くためにある。
// つまり「どう頼むか」を2か所が必要とする。どちらかに書くと、もう一方に写すことになり、
// パラメータを1つ足したときに片方だけ直る。渡す値が欠けてもエラーは出ず、
// 条件が永久に当たらないだけなので気づけない。1つのファイルを両方から読む。
// ──────────────────────────────────────────────

/** 先導役が先に頼んだ結果を、描画本体が受け取るための置き場。
 *  先導役だけが書き、描画本体だけが読む。無ければ描画本体が自分で頼む（単独でも動く）。 */
interface BootStash {
  /** 鍵 → 返ってきたJSON（失敗・未完了は null）。 */
  docs?: Record<string, Promise<unknown>>;
}
function stash(): BootStash {
  const w = window as unknown as { __clBoot?: BootStash };
  if (!w.__clBoot) w.__clBoot = {};
  return w.__clBoot;
}

/** 導線パラメータ（UTM / クリックID）を着地ページURLから収集。導線×LP集計のキー。 */
// "geo" は地域出し分け/計測の明示指定（広告リンクに ?geo=東京都 等）。/api/split と track に転送。
const SRC_KEYS = ["utm_source", "utm_medium", "utm_campaign", "gclid", "gbraid", "wbraid", "fbclid", "yclid", "geo"];
export function srcObj(): Record<string, string> {
  const o: Record<string, string> = {};
  try {
    const q = new URLSearchParams(location.search);
    for (const k of SRC_KEYS) {
      const v = q.get(k);
      if (v) o[k] = v.slice(0, 80);
    }
  } catch {
    /* ignore */
  }
  return o;
}

/** 競合トラッキング用：外部サイト由来のリファラー「ホスト名だけ」（自サイト内遷移・空は除外）。 */
export function refHost(): string {
  try {
    const r = document.referrer;
    if (!r) return "";
    const h = new URL(r).hostname;
    if (!h || h === location.hostname) return "";
    return h.slice(0, 120);
  } catch {
    return "";
  }
}

export function srcQuery(): string {
  const s = new URLSearchParams(srcObj()).toString();
  return s ? `&${s}` : "";
}

/** いま表示しているページ。貼り付け型では、ここを渡さないとサーバーは自分のAPIのパスしか知らない。
 *
 *  【なぜ要るか】
 *  ・条件の「サイト（ドメイン）」「URLパス」「ページグループ」が、渡さないと永久に当たらない。
 *  ・閲覧の記録の鍵がサイト計測タグと食い違い、同じページが2件になって温度が二重に乗る。
 *  クエリは送らない（個人を含む値が混ざり得るため）。 */
export function pageObj(): { h: string; p: string; t: string } {
  try {
    return {
      h: (location.hostname || "").slice(0, 120),
      p: (location.pathname || "/").slice(0, 200),
      t: (document.title || "").slice(0, 120),
    };
  } catch {
    return { h: "", p: "/", t: "" };
  }
}
export function pageQuery(): string {
  const o = pageObj();
  return `&h=${encodeURIComponent(o.h)}&p=${encodeURIComponent(o.p)}&t=${encodeURIComponent(o.t)}`;
}

/** 第一者訪問者ID(cl_uid)。URL ?cl_uid（クロスドメイン受け渡し）> cookie > localStorage > の順で解決。
 *  ★cookie にも保存することで、同一ドメインの他ページ／measure.js と cl_uid を共有し「同一ドメイン回遊」を
 *   一貫した1人として追える（localStorage は origin 隔離＝measure.js の cookie と繋がらないため）。 */
const cleanUid = (s: string): string => s.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40);
function readUidCookie(): string {
  try { const m = /(?:^|; )cl_uid=([A-Za-z0-9_-]{1,40})/.exec(document.cookie || ""); return m ? m[1] : ""; } catch { return ""; }
}
function writeUidStores(uid: string): void {
  const u = cleanUid(uid);
  if (!u) return;
  try { localStorage.setItem("cl_uid", u); } catch { /* ignore */ }
  try { document.cookie = "cl_uid=" + u + ";path=/;max-age=63072000;samesite=lax"; } catch { /* ignore */ }
}
export function clUid(): string {
  try {
    const fromUrl = cleanUid(new URLSearchParams(location.search).get("cl_uid") || "");
    if (fromUrl) { writeUidStores(fromUrl); return fromUrl; }
    const fromCookie = readUidCookie();
    if (fromCookie) { try { localStorage.setItem("cl_uid", fromCookie); } catch { /* ignore */ } return fromCookie; }
    const fromLs = cleanUid((() => { try { return localStorage.getItem("cl_uid") || ""; } catch { return ""; } })());
    if (fromLs) { writeUidStores(fromLs); return fromLs; } // localStorage のみ保持→cookieへ昇格（measure.jsと共有）
    return "";
  } catch {
    return "";
  }
}
export function setUid(uid: string): void {
  if (uid) writeUidStores(uid);
}

/** 出すLPを選んだ結果を覚えておく鍵（同じ人には同じLPを出す）。 */
export const variantKey = (projectId: string): string => `clv_${projectId}`;
function storedVariant(projectId: string): string {
  try { return localStorage.getItem(variantKey(projectId)) || ""; } catch { return ""; }
}

/** どのLPを出すかと、その中身を1回で貰う口。
 *  doc=1 を付けると中身まで返る。付けない呼び方も受け付ける（古いタグのため）。 */
export function splitUrl(apiBase: string, projectId: string): string {
  const uid = clUid();
  return `${apiBase}/api/split/${encodeURIComponent(projectId)}?doc=1&v=${encodeURIComponent(storedVariant(projectId))}`
    + `${srcQuery()}${pageQuery()}${uid ? `&cl_uid=${encodeURIComponent(uid)}` : ""}`;
}

/** LPを名指しで出すとき（data-id）の口。出し分けはしない。 */
export function lpUrl(apiBase: string, id: string): string {
  return `${apiBase}/api/lp/${encodeURIComponent(id)}`;
}

/** 置き場の鍵。配信元が違えば別物として扱う（1ページに複数の埋め込みがある場合）。 */
export const askKey = (apiBase: string, kind: "p" | "l", idOrPid: string): string => `${kind}:${apiBase}:${idOrPid}`;

/** 中身を頼む。すでに先導役が頼んでいればそれを待ち、無ければここで頼む。
 *
 *  【なぜ失敗を例外にしないか】
 *  先導役の分が失敗しても、描画本体が自分でもう一度頼めば出せる。
 *  例外にすると、置き場を見た側がそのまま諦めてしまう。 */
export async function ask(apiBase: string, kind: "p" | "l", idOrPid: string): Promise<unknown> {
  const key = askKey(apiBase, kind, idOrPid);
  const s = stash();
  const pre = s.docs && key in s.docs ? s.docs[key] : undefined;
  if (pre) {
    delete s.docs![key]; // 1回ぶんだけ。再マウント（ビルダーのプレビュー等）では取り直す
    const j = await pre;
    if (j) return j;
    // 先導役の分が失敗した。黙って何も出さないより、ここでもう一度頼む。
  }
  return fetchJson(kind === "p" ? splitUrl(apiBase, idOrPid) : lpUrl(apiBase, idOrPid));
}

/** 失敗を null にして返す。ここで投げると、先導役の置き場に未処理の失敗が残る。 */
export function fetchJson(url: string): Promise<unknown> {
  return fetch(url, { headers: { Accept: "application/json" } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** 先導役が、描画本体を待たずに中身を頼む。 */
export function prefetch(apiBase: string, kind: "p" | "l", idOrPid: string): void {
  const key = askKey(apiBase, kind, idOrPid);
  const s = stash();
  if (!s.docs) s.docs = {};
  if (key in s.docs) return;
  s.docs[key] = fetchJson(kind === "p" ? splitUrl(apiBase, idOrPid) : lpUrl(apiBase, idOrPid));
}
